import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, CheckCircle, GraduationCap, Globe, MapPin, Train, DollarSign, Coffee, Briefcase, Clock, Video } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import claraPortrait from "@/assets/clara-portrait-final.png";
import heroBackground from "@/assets/hero-background.jpg";

export const faqItems = [
  {
    question: "Você atende presencialmente em Sydney?",
    answer: "Meu atendimento presencial é em Brisbane, no consultório em Hamilton. Para quem mora em Sydney, o atendimento é online, por videochamada. A qualidade do acompanhamento é a mesma, com a praticidade de não precisar se deslocar."
  },
  {
    question: "O plano alimentar considera os preços de Sydney?",
    answer: "Sim. Sydney é uma das cidades mais caras da Austrália, e isso é levado em conta ao montar o plano. Priorizo opções com bom custo-benefício e oriento sobre onde encontrar preços melhores."
  },
  {
    question: "Como funciona a consulta online?",
    answer: "É por videochamada, com dia e horário marcados, e segue o mesmo formato da consulta presencial: conversamos sobre sua rotina e seus objetivos, e eu oriento você em cada etapa do processo. Depois, você recebe tudo na plataforma."
  },
  {
    question: "Como funciona a primeira consulta?",
    answer: "É uma conversa de cerca de 30 minutos, sem custo, por videochamada. Eu entendo seus objetivos e sua rotina em Sydney e explico como funciona o acompanhamento. Depois você decide, com calma, se quer seguir."
  },
  {
    question: "Como funciona o fuso horário?",
    answer: "Brisbane não tem horário de verão e Sydney tem, de outubro a abril. Nesse período, meus horários em Sydney ficam uma hora à frente: terça e sexta das 10h às 19h, quarta e quinta das 10h às 18h e sábado das 11h às 15h. No resto do ano é o mesmo horário de Brisbane. A agenda online já mostra tudo no seu fuso."
  },
  {
    question: "Você já atende pacientes em Sydney?",
    answer: "Sim. Parte dos meus pacientes mora em Sydney e em outras cidades de NSW e é atendida só online. O acompanhamento foi desenhado para isso: videochamada, plano alimentar na plataforma e WhatsApp entre as consultas, nos horários que cabem na rotina de quem está aí."
  },
  {
    question: "Tem reembolso do Medicare ou do plano de saúde?",
    answer: "Medicare não: o reembolso do Medicare é exclusivo de dietitians credenciadas (APD), e eu sou nutricionista. Plano de saúde privado depende do seu fundo e do pacote de extras; eu forneço o recibo e você confere a cobertura com o plano. Meus valores já são pensados para funcionar sem reembolso."
  },
  {
    question: "O plano precisa de produtos brasileiros?",
    answer: "Não. Tudo é montado com o que você acha no Coles, Woolworths, Aldi e Harris Farm. Produtos brasileiros entram como opção, nunca como obrigação. No blog eu mostro o que vale a pena comprar nos mercados australianos."
  },
  {
    question: "Posso trocar receitas durante o acompanhamento?",
    answer: "Sim. O plano não é fixo, e eu ajusto sempre que precisar. Se uma receita não funcionou, se você descobriu um ingrediente novo ou se a rotina mudou, nós adaptamos."
  },
  {
    question: "Quanto tempo leva para ver resultados?",
    answer: "Os acompanhamentos normalmente têm um período mínimo de 3 meses, mas o tempo para ver resultados depende de cada pessoa e de cada plano. Muitos pacientes relatam, já nas primeiras semanas, mais disposição e uma relação mais tranquila com a comida."
  },
];

const desafiosSydney = [
  {
    icon: DollarSign,
    title: "Custo de vida alto e alimentação",
    description: "Sydney é uma das cidades mais caras do mundo. Comer bem sem estourar o orçamento exige planejamento. O plano é montado com opções nutritivas e acessíveis, com compras planejadas no Coles, no Woolworths e em outros mercados da região."
  },
  {
    icon: Train,
    title: "Rotina corrida e deslocamento",
    description: "Quem mora em Western Sydney e trabalha no CBD sabe o que é passar horas no trem. Com pouco tempo para cozinhar, meal prep e refeições rápidas se tornam essenciais, e o plano é pensado para essa realidade."
  },
  {
    icon: Coffee,
    title: "Cultura gastronômica diversa",
    description: "De Marrickville a Cabramatta, Sydney tem uma cena gastronômica muito diversa. Essa variedade é ótima, mas pode dificultar as escolhas do dia a dia. Eu ajudo você a escolher bem sem abrir mão do prazer de comer."
  },
  {
    icon: Briefcase,
    title: "Trabalho intenso e estresse",
    description: "Muitos brasileiros em Sydney trabalham em áreas de alta demanda. O estresse afeta diretamente a alimentação, o sono e o peso. O acompanhamento considera sua rotina de trabalho para criar um plano sustentável."
  },
];

const passosSydney = [
  {
    icon: Calendar,
    title: "1. Você escolhe o horário",
    description: "A agenda online mostra os horários já no fuso de Sydney. A consulta inicial é por videochamada e dura cerca de 30 minutos.",
  },
  {
    icon: Video,
    title: "2. Consulta por videochamada",
    description: "Avaliação completa, como numa consulta presencial. Você recebe o link do encontro por e-mail depois de agendar.",
  },
  {
    icon: MessageCircle,
    title: "3. Plano e acompanhamento",
    description: "O plano alimentar fica na plataforma e os ajustes acontecem pelo WhatsApp entre as consultas, com produtos do Coles, Woolworths, Aldi e Harris Farm.",
  },
];

const beneficios = [
  "Plano alimentar com produtos dos mercados de Sydney (Coles, Woolworths, Aldi)",
  "Planejamento que considera o custo de vida de Sydney",
  "Acesso à plataforma, com conteúdo prático",
  "Contato direto comigo por WhatsApp entre as consultas",
  "Planos adaptados para quem tem rotina corrida",
  "Consultas online no horário que funciona para você",
];

const CTAButtons = ({ openDialog }: { openDialog: () => void }) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
    <Button
      size="lg"
      variant="gradient"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      style={{ background: 'var(--gradient-green)' }}
      onClick={openDialog}
    >
      <Calendar className="mr-2 h-5 w-5" />
      Agendar consulta
    </Button>
    <Button
      size="lg"
      variant="gradient-outline"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gradient-green)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
      onClick={() => window.open('https://wa.me/61423038977', '_blank')}
    >
      <MessageCircle className="mr-2 h-5 w-5" />
      Falar no WhatsApp
    </Button>
  </div>
);

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <>

      <div className="min-h-screen">
        <Navigation />

        <main>
          {/* HERO */}
          <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
            <div
              className="absolute inset-0 opacity-15"
              style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
              <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                Clara Camille · Atendimento online
              </p>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">
                Nutricionista brasileira em Sydney
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">
                Acompanhamento nutricional online, em português, para brasileiros que moram em Sydney. Plano alimentar feito com os produtos que você encontra aí e pensado para a sua rotina.
              </p>
              <CTAButtons openDialog={openDialog} />
              <p className="text-sm text-muted-foreground font-sans">
                Consulta inicial de 30 minutos, sem custo.
              </p>
            </div>
          </section>

          {/* DESAFIOS DE SYDNEY */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Vida em Sydney
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Os desafios de comer bem morando em Sydney
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
                {desafiosSydney.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex gap-4 sm:gap-5 p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-primary/5 hover:bg-primary/10 transition-colors">
                      <div className="flex-shrink-0">
                        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-primary/15 flex items-center justify-center">
                          <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-primary" strokeWidth={1.5} />
                        </div>
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* CONTEÚDO LOCAL */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-8 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Entendendo sua realidade
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground leading-tight">
                  Nutrição adaptada à vida em Sydney
                </h2>
              </div>

              <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed max-w-3xl mx-auto text-center mb-8 sm:mb-12">
                Sydney é a maior cidade da Austrália e abriga uma grande comunidade brasileira. De Bondi a Parramatta, as dificuldades com a alimentação são parecidas.
              </p>

              <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 max-w-3xl mx-auto">
                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Onde os brasileiros moram</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Bondi, Marrickville, Strathfield, Parramatta, Western Sydney: o plano considera os mercados acessíveis da sua região.
                  </p>
                </div>

                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <DollarSign className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Custo de vida alto</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Como Sydney é cara, o plano prioriza o custo-benefício e inclui mercados asiáticos, como os de Eastwood e Cabramatta, para comprar legumes frescos e baratos.
                  </p>
                </div>

                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <Briefcase className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Plano para a sua rotina</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Trabalha no CBD, estuda na UNSW ou na UTS, mora em uma shared house em Bondi? Eu adapto o plano para funcionar na <span className="italic gradient-text font-medium">sua</span> vida.
                  </p>
                </div>

                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <Train className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Deslocamento longo</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    As horas no trem entre Western Sydney e o CBD deixam pouco tempo para cozinhar. O plano inclui meal prep e refeições rápidas para esses dias.
                  </p>
                </div>
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* O QUE VOCÊ RECEBE */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  O que está incluído
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  O que você recebe no acompanhamento
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
                {beneficios.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-primary/5">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* CREDENCIAIS */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Quem te acompanha
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Conheça a Clara Camille
                </h2>
              </div>

              <div
                className="rounded-2xl sm:rounded-3xl overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(16px) saturate(1.3)',
                  WebkitBackdropFilter: 'blur(16px) saturate(1.3)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
                }}
              >
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-2/5 flex-shrink-0">
                    <div className="aspect-[3/4] sm:h-full w-full overflow-hidden">
                      <img
                        src={claraPortrait}
                        alt="Clara Camille, nutricionista brasileira para pacientes em Sydney"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <div className="sm:w-3/5 p-6 sm:p-10 flex flex-col justify-center space-y-6">
                    <div className="space-y-1">
                      <h3 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Clara Camille</h3>
                      <p className="text-sm sm:text-base text-primary font-typewriter font-semibold">Nutricionista · Austrália</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {[
                        { icon: GraduationCap, text: "Formação em Nutrição" },
                        { icon: Globe, text: "Atendimento em português" },
                        { icon: MapPin, text: "Online para todo o estado de NSW" },
                      ].map((cred, i) => (
                        <div key={i} className="flex items-start gap-2.5 p-3 rounded-xl bg-primary/5">
                          <cred.icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                          <span className="text-xs sm:text-sm text-foreground font-sans leading-snug">{cred.text}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                      Acompanho brasileiros em Sydney que querem cuidar da alimentação com alguém que conhece a realidade de ser imigrante. Tudo online, em português e sem julgamento.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* COMO FUNCIONA EM SYDNEY */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Passo a passo
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Como funciona para quem mora em Sydney
                </h2>
              </div>

              <div className="grid gap-4 sm:gap-6 sm:grid-cols-3">
                {passosSydney.map((item, index) => (
                  <div key={index} className="rounded-xl sm:rounded-2xl bg-background border border-border/50 p-5 sm:p-6 space-y-3">
                    <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-foreground font-sans">{item.title}</h3>
                    <p className="text-sm text-muted-foreground font-sans leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 sm:mt-10 rounded-xl sm:rounded-2xl bg-primary/5 p-5 sm:p-6">
                <div className="flex items-center gap-2.5 mb-2">
                  <Clock className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                  <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Horários no fuso de Sydney</h3>
                </div>
                <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                  Atendo terça e sexta das 9h às 18h, quarta e quinta das 9h às 17h e sábado das 10h às 14h, no horário de Brisbane. De outubro a abril, com o horário de verão de NSW, isso vira 10h às 19h, 10h às 18h e 11h às 15h em Sydney. A agenda online já mostra os horários no seu fuso.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Tire suas dúvidas
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Dúvidas frequentes sobre nutrição em Sydney
                </h2>
              </div>

              <Accordion type="single" collapsible className="space-y-3">
                {faqItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="rounded-xl border border-border/50 bg-card px-5 sm:px-6"
                  >
                    <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-medium text-foreground hover:no-underline py-4 sm:py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* CTA FINAL */}
          <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
            <div
              className="absolute inset-0 blur-sm scale-105"
              style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            />
            <div className="absolute inset-0 opacity-80" style={{ background: 'var(--gradient-green)' }} />
            <div className="container mx-auto px-4 sm:px-6 relative z-10 flex justify-center">
              <div
                className="max-w-2xl w-full text-center space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl px-5 py-10 sm:px-8 sm:py-12 lg:px-14 lg:py-16"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(24px) saturate(1.4)',
                  WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-typewriter font-bold text-white leading-tight">
                  Mora em Sydney? Vamos conversar
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 font-sans leading-relaxed">
                  Na consulta inicial, entendo sua rotina e seus objetivos e explico como o acompanhamento funciona online, no horário que for melhor para você.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto"
                    onClick={openDialog}
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Agendar consulta
                  </Button>
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto"
                    style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(12px)', color: 'white' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                    onClick={() => window.open('https://wa.me/61423038977', '_blank')}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Falar no WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* LINKS INTERNOS */}
          <section className="bg-card py-10 sm:py-14">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center space-y-4">
              <p className="text-sm text-muted-foreground font-sans">Navegue pelo site:</p>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                <Link to="/" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Página inicial</Link>
                <span className="text-border">·</span>
                <Link to="/nutricionista-brasileira-na-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Nutricionista na Austrália</Link>
                <span className="text-border">·</span>
                <Link to="/nutricionista-brasileira-brisbane" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Nutricionista em Brisbane</Link>
                <span className="text-border">·</span>
                <Link to="/nutricionista-brasileira-melbourne" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Nutricionista em Melbourne</Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const NutricionistaBrasileiraSydney = () => (
  <ScheduleProvider>
    <PageContent />
  </ScheduleProvider>
);

export default NutricionistaBrasileiraSydney;
